// =============================================================================
// MeuDriver — Admin Dashboard / Pending instructors approval table
// =============================================================================

import { useState } from 'react'
import { toast } from 'sonner'
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { supabase } from '@/lib/supabase'
import { formatDate } from '@/lib/utils'
import type { InstructorWithProfile } from '@/types/database'

interface Props {
  pending: InstructorWithProfile[]
  loading: boolean
  totalPending: number
  onChanged: () => Promise<void> | void
}

interface RejectDialogState {
  open: boolean
  instructorId: string
  instructorName: string
}

export function AdminPendingInstructors({ pending, loading, totalPending, onChanged }: Props) {
  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState>({
    open: false,
    instructorId: '',
    instructorName: '',
  })
  const [rejectReason, setRejectReason] = useState('')
  const [rejectingId, setRejectingId] = useState<string | null>(null)

  async function handleApprove(instructorId: string) {
    setApprovingId(instructorId)
    try {
      const { error } = await supabase.from('instructors').update({ status: 'approved' }).eq('id', instructorId)
      if (error) throw error
      toast.success('Instrutor aprovado com sucesso!')
      await onChanged()
    } catch {
      toast.error('Erro ao aprovar instrutor')
    } finally {
      setApprovingId(null)
    }
  }

  function openRejectDialog(instructor: InstructorWithProfile) {
    setRejectReason('')
    setRejectDialog({
      open: true,
      instructorId: instructor.id,
      instructorName: instructor.profile?.full_name ?? 'Instrutor',
    })
  }

  async function handleReject() {
    setRejectingId(rejectDialog.instructorId)
    try {
      const { error } = await supabase.from('instructors').update({ status: 'suspended' }).eq('id', rejectDialog.instructorId)
      if (error) throw error
      toast.success('Instrutor rejeitado.')
      setRejectDialog({ open: false, instructorId: '', instructorName: '' })
      await onChanged()
    } catch {
      toast.error('Erro ao rejeitar instrutor')
    } finally {
      setRejectingId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-orange-500" />
          Instrutores Aguardando Aprovação
          {totalPending > 0 && (
            <Badge variant="outline" className="ml-1 border-orange-400 bg-orange-50 text-orange-700">
              {totalPending}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-md" />)}
          </div>
        ) : pending.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
            <CheckCircle className="mb-2 h-8 w-8 text-green-500" />
            <p>Nenhum instrutor aguardando aprovação.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-3 pr-4 font-medium">Nome</th>
                  <th className="pb-3 pr-4 font-medium">Cidade</th>
                  <th className="pb-3 pr-4 font-medium">CNH</th>
                  <th className="pb-3 pr-4 font-medium">Cadastrado em</th>
                  <th className="pb-3 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pending.map((instructor) => (
                  <tr key={instructor.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 pr-4 font-medium">{instructor.profile?.full_name ?? '—'}</td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {instructor.profile?.city ?? '—'}
                      {instructor.profile?.state ? `, ${instructor.profile.state}` : ''}
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs">{instructor.cnh_number ?? '—'}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{formatDate(instructor.created_at)}</td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          disabled={approvingId === instructor.id}
                          onClick={() => handleApprove(instructor.id)}
                        >
                          {approvingId === instructor.id ? 'Aprovando...' : 'Aprovar'}
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => openRejectDialog(instructor)}>
                          Rejeitar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog
        open={rejectDialog.open}
        onOpenChange={(open) => setRejectDialog((prev) => ({ ...prev, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Rejeitar Instrutor
            </DialogTitle>
            <DialogDescription>
              Você está prestes a rejeitar o cadastro de <strong>{rejectDialog.instructorName}</strong>.
              O instrutor ficará com status <em>Suspenso</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason">Motivo (opcional)</Label>
            <Textarea
              id="reject-reason"
              placeholder="Descreva o motivo da rejeição..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, instructorId: '', instructorName: '' })}>
              Cancelar
            </Button>
            <Button variant="destructive" disabled={rejectingId === rejectDialog.instructorId} onClick={handleReject}>
              {rejectingId === rejectDialog.instructorId ? 'Rejeitando...' : 'Confirmar Rejeição'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
